import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { uploadImage } from '../../lib/storage';
import { translateObject } from '../../lib/translate';
import { 
    Layout, 
    Save, 
    Globe, 
    Eye, 
    EyeOff, 
    Upload, 
    Loader2, 
    CheckCircle2, 
    AlertCircle,
    Smartphone,
    Monitor,
    ChevronRight,
    Sparkles
} from 'lucide-react';
import { Language, LANGUAGE_NAMES } from '../../translations';

interface HomeSection {
    id: string;
    is_visible: boolean;
    bg_image_url?: string;
    content_es: any;
    content_en: any;
    content_pt?: any;
    content_fr?: any;
    content_it?: any;
    content_de?: any;
    updated_at?: string;
}

const HomeEditor: React.FC = () => {
    const { theme } = useOutletContext<{ theme: 'light' | 'dark' }>();
    const isDark = theme === 'dark';

    const labelClass = `text-[10px] font-bold uppercase tracking-widest block transition-colors duration-200 ${
        isDark ? 'text-gray-400' : 'text-gray-600'
    }`;
    const inputClass = `w-full border px-4 py-3 text-xs outline-none focus:border-[#00d4ff] focus:ring-2 focus:ring-[#00d4ff]/10 transition-all duration-200 rounded-[8px] ${
        isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 shadow-sm'
    }`;
    const textareaClass = `w-full border px-4 py-3 text-xs outline-none focus:border-[#00d4ff] focus:ring-2 focus:ring-[#00d4ff]/10 transition-all duration-200 rounded-[8px] min-h-[100px] resize-y ${
        isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 shadow-sm'
    }`;

    const [sections, setSections] = useState<HomeSection[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState<'home' | 'about'>('home');
    const [activeSectionId, setActiveSectionId] = useState<string>('hero');
    const [previewMode, setPreviewMode] = useState<'mobile' | 'desktop'>('desktop');
    const [notification, setNotification] = useState<{ message: string, type: 'success' | 'error' } | null>(null);

    // Draft state for form
    const [draftIsVisible, setDraftIsVisible] = useState(true);
    const [draftBgImage, setDraftBgImage] = useState('');
    const [draftContent, setDraftContent] = useState<any>({});
    const [draftAbout, setDraftAbout] = useState<any>({});
    const [draftPhilosophy, setDraftPhilosophy] = useState<any>({});

    const [uploadingAboutImages, setUploadingAboutImages] = useState(false);

    const handleAboutImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setUploadingAboutImages(true);
        try {
            const filesArray = Array.from(files);
            const successfulUrls: string[] = [];
            const errors: string[] = [];

            for (const file of filesArray) {
                try {
                    const url = await uploadImage(file);
                    successfulUrls.push(url);
                } catch (err: any) {
                    errors.push(err.message || `Error al subir "${file.name}"`);
                }
            }
            
            if (successfulUrls.length > 0) {
                setDraftAbout((prev: any) => {
                    const prevGallery = prev.gallery || [];
                    return {
                        ...prev,
                        gallery: [...prevGallery, ...successfulUrls]
                    };
                });
            }

            if (errors.length > 0) {
                showNotification(errors.join('. '), 'error');
            } else if (successfulUrls.length > 0) {
                showNotification(`${successfulUrls.length} foto(s) subida(s) con éxito`, 'success');
            }
        } catch (error: any) {
            console.error('Error uploading about images:', error);
            showNotification(error?.message || 'Error al subir las imágenes', 'error');
        } finally {
            setUploadingAboutImages(false);
            if (e.target) e.target.value = '';
        }
    };

    const removeAboutImage = (urlToRemove: string) => {
        setDraftAbout((prev: any) => {
            const prevGallery = prev.gallery || [];
            return {
                ...prev,
                gallery: prevGallery.filter((url: string) => url !== urlToRemove)
            };
        });
    };

    const handleAboutFieldChange = (key: string, value: string) => {
        setDraftAbout((prev: any) => ({
            ...prev,
            [key]: value
        }));
    };

    const handlePhilosophyFieldChange = (key: string, value: string) => {
        setDraftPhilosophy((prev: any) => ({
            ...prev,
            [key]: value
        }));
    };

    const activeSection = sections.find(s => s.id === activeSectionId);

    // Fetch Home Sections
    const fetchSections = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('home_sections')
                .select('*')
                .order('id');
            
            if (error) throw error;
            if (data) {
                setSections(data);
                // Load default values for hero
                const heroSec = data.find(s => s.id === 'hero');
                if (heroSec) {
                    setDraftIsVisible(heroSec.is_visible);
                    setDraftBgImage(heroSec.bg_image_url || '');
                    setDraftContent(heroSec.content_es || {});
                }
                const aboutSec = data.find(s => s.id === 'about');
                if (aboutSec) setDraftAbout(aboutSec.content_es || {});
                const philSec = data.find(s => s.id === 'philosophy');
                if (philSec) setDraftPhilosophy(philSec.content_es || {});
            }
        } catch (err: any) {
            console.error('Error fetching home sections:', err);
            showNotification('Error al cargar secciones de la Home', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSections();
    }, []);

    // Change section handler
    const handleSelectSection = (id: string) => {
        setActiveSectionId(id);
        const sec = sections.find(s => s.id === id);
        if (sec) {
            setDraftIsVisible(sec.is_visible !== undefined ? sec.is_visible : true);
            setDraftBgImage(sec.bg_image_url || '');
            if (id === 'footer') {
                setDraftContent({
                    manifesto: sec.content_es?.manifesto || 'Nuestro compromiso se centra en la montaña y la autenticidad. Brindamos experiencias que transforman a través del respeto y la conexión profunda.',
                    location: sec.content_es?.location || 'Villa Cerro Castillo, Aysén, Chile',
                    email: sec.content_es?.email || 'hello@fisuraexpeditions.com'
                });
            } else {
                setDraftContent(sec.content_es || {});
            }
        } else if (id === 'footer') {
            setDraftIsVisible(true);
            setDraftBgImage('');
            setDraftContent({
                manifesto: 'Nuestro compromiso se centra en la montaña y la autenticidad. Brindamos experiencias que transforman a través del respeto y la conexión profunda.',
                location: 'Villa Cerro Castillo, Aysén, Chile',
                email: 'hello@fisuraexpeditions.com'
            });
        } else {
            setDraftIsVisible(true);
            setDraftBgImage('');
            setDraftContent({});
        }
    };

    // Handle field updates
    const handleFieldChange = (key: string, value: string) => {
        setDraftContent((prev: any) => ({
            ...prev,
            [key]: value
        }));
    };

    // Show Notification helper
    const showNotification = (message: string, type: 'success' | 'error') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 4000);
    };

    // Handle Image Upload
    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const publicUrl = await uploadImage(file, 'expeditions', `home/${activeSectionId}`);
            setDraftBgImage(publicUrl);
            showNotification('Imagen subida con éxito', 'success');
        } catch (err: any) {
            console.error('Error uploading image:', err);
            showNotification(err?.message || 'Error al subir la imagen', 'error');
        } finally {
            setUploading(false);
            if (e.target) e.target.value = '';
        }
    };

    // Save Section Content
    const handleSave = async () => {
        setSaving(true);
        try {
            if (activeTab === 'about') {
                const updateAbout: any = {
                    is_visible: true,
                    content_es: draftAbout,
                    updated_at: new Date().toISOString()
                };
                const updatePhil: any = {
                    is_visible: true,
                    content_es: draftPhilosophy,
                    updated_at: new Date().toISOString()
                };

                const targetLanguages: ('en' | 'pt' | 'fr' | 'it' | 'de')[] = ['en', 'pt', 'fr', 'it', 'de'];
                await Promise.all([
                    Promise.all(targetLanguages.map(async (lang) => {
                        try {
                            updateAbout[`content_${lang}`] = await translateObject(draftAbout, lang);
                        } catch {
                            updateAbout[`content_${lang}`] = draftAbout;
                        }
                    })),
                    Promise.all(targetLanguages.map(async (lang) => {
                        try {
                            updatePhil[`content_${lang}`] = await translateObject(draftPhilosophy, lang);
                        } catch {
                            updatePhil[`content_${lang}`] = draftPhilosophy;
                        }
                    }))
                ]);

                const { error: errAbout } = await supabase
                    .from('home_sections')
                    .update(updateAbout)
                    .eq('id', 'about');
                if (errAbout) throw errAbout;

                const { error: errPhil } = await supabase
                    .from('home_sections')
                    .update(updatePhil)
                    .eq('id', 'philosophy');
                if (errPhil) throw errPhil;

                showNotification('Página Nosotros guardada y traducida con éxito', 'success');

                setSections(prev => prev.map(s => {
                    if (s.id === 'about') {
                        return { ...s, ...updateAbout };
                    }
                    if (s.id === 'philosophy') {
                        return { ...s, ...updatePhil };
                    }
                    return s;
                }));
            } else {
                if (!activeSectionId) return;

                const updateObj: any = {
                    id: activeSectionId,
                    is_visible: draftIsVisible,
                    bg_image_url: draftBgImage || null,
                    content_es: draftContent,
                    updated_at: new Date().toISOString()
                };

                const targetLanguages: ('en' | 'pt' | 'fr' | 'it' | 'de')[] = ['en', 'pt', 'fr', 'it', 'de'];
                await Promise.all(
                    targetLanguages.map(async (lang) => {
                        try {
                            const translated = await translateObject(draftContent, lang);
                            if (draftContent.email) {
                                translated.email = draftContent.email;
                            }
                            updateObj[`content_${lang}`] = translated;
                        } catch {
                            updateObj[`content_${lang}`] = draftContent;
                        }
                    })
                );

                const { error } = await supabase
                    .from('home_sections')
                    .upsert(updateObj);
                if (error) throw error;

                showNotification('Sección guardada y traducida con éxito', 'success');

                setSections(prev => {
                    const exists = prev.some(s => s.id === activeSectionId);
                    if (exists) {
                        return prev.map(s => s.id === activeSectionId ? { ...s, ...updateObj } : s);
                    }
                    return [...prev, updateObj];
                });

                if (activeSectionId === 'philosophy') {
                    setDraftPhilosophy(draftContent);
                }
            }
        } catch (err: any) {
            console.error('Error saving sections:', err);
            showNotification(err?.message || 'Error al guardar los cambios', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Section Schemas / Form definitions
    const renderFormFields = () => {
        switch (activeSectionId) {
            case 'hero':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className={labelClass}>Tagline Principal (Hero)</label>
                            <input 
                                type="text"
                                className={inputClass}
                                value={draftContent.tagline || ''}
                                onChange={(e) => handleFieldChange('tagline', e.target.value)}
                                placeholder="La Cordillera del Castillo te espera..."
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Texto del Botón (CTA)</label>
                            <input 
                                type="text"
                                className={inputClass}
                                value={draftContent.cta || ''}
                                onChange={(e) => handleFieldChange('cta', e.target.value)}
                                placeholder="Ver expediciones"
                            />
                        </div>
                    </div>
                );
            case 'about':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className={labelClass}>Categoría (Texto pequeño superior)</label>
                            <input 
                                type="text"
                                className={inputClass}
                                value={draftContent.category || ''}
                                onChange={(e) => handleFieldChange('category', e.target.value)}
                                placeholder="Nuestra Historia"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Título Destacado</label>
                            <input 
                                type="text"
                                className={inputClass}
                                value={draftContent.title || ''}
                                onChange={(e) => handleFieldChange('title', e.target.value)}
                                placeholder="NACIMOS EN EL GRANITO DE AYSÉN"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Descripción (Textos extensos)</label>
                            <textarea 
                                className={textareaClass}
                                value={draftContent.text || ''}
                                onChange={(e) => handleFieldChange('text', e.target.value)}
                                placeholder="Detalla la historia..."
                            />
                        </div>
                        
                        {/* Certifications / Trust Badges */}
                        <div className="border-t pt-4 mt-4 border-gray-100/10 space-y-3">
                            <span className="text-[10px] text-[#00d4ff] font-bold tracking-[0.2em] uppercase">Certificaciones y Badges (Nosotros)</span>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Guía Certificado</label>
                                    <input 
                                        type="text"
                                        className={inputClass}
                                        value={draftContent.trust_angm || ''}
                                        onChange={(e) => handleFieldChange('trust_angm', e.target.value)}
                                        placeholder="Guía Certificado ANGM"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Soporte Vital</label>
                                    <input 
                                        type="text"
                                        className={inputClass}
                                        value={draftContent.trust_wfr || ''}
                                        onChange={(e) => handleFieldChange('trust_wfr', e.target.value)}
                                        placeholder="Soporte Vital WFR"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Satelital</label>
                                    <input 
                                        type="text"
                                        className={inputClass}
                                        value={draftContent.trust_gps || ''}
                                        onChange={(e) => handleFieldChange('trust_gps', e.target.value)}
                                        placeholder="Satelital InReach"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Idioma</label>
                                    <input 
                                        type="text"
                                        className={inputClass}
                                        value={draftContent.trust_lang || ''}
                                        onChange={(e) => handleFieldChange('trust_lang', e.target.value)}
                                        placeholder="Multilingüe"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Grupos</label>
                                    <input 
                                        type="text"
                                        className={inputClass}
                                        value={draftContent.trust_groups || ''}
                                        onChange={(e) => handleFieldChange('trust_groups', e.target.value)}
                                        placeholder="Grupos Íntimos"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Medioambiente</label>
                                    <input 
                                        type="text"
                                        className={inputClass}
                                        value={draftContent.trust_ndr || ''}
                                        onChange={(e) => handleFieldChange('trust_ndr', e.target.value)}
                                        placeholder="Sin Rastro"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'philosophy':
                return (
                    <div className="space-y-4">
                        <div className="border-b pb-4 mb-4 border-gray-100/10">
                            <span className="text-[10px] text-[#00d4ff] font-bold tracking-[0.2em] uppercase">Bloque 01: Filosofía</span>
                            <div className="mt-2 space-y-2">
                                <label className={labelClass}>Título</label>
                                <input 
                                    type="text"
                                    className={inputClass}
                                    value={draftContent.phil_title || ''}
                                    onChange={(e) => handleFieldChange('phil_title', e.target.value)}
                                />
                                <label className={labelClass}>Descripción</label>
                                <textarea 
                                    className={textareaClass}
                                    value={draftContent.phil_text || ''}
                                    onChange={(e) => handleFieldChange('phil_text', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="border-b pb-4 mb-4 border-gray-100/10">
                            <span className="text-[10px] text-[#00d4ff] font-bold tracking-[0.2em] uppercase">Bloque 02: Ubicación</span>
                            <div className="mt-2 space-y-2">
                                <label className={labelClass}>Título</label>
                                <input 
                                    type="text"
                                    className={inputClass}
                                    value={draftContent.loc_title || ''}
                                    onChange={(e) => handleFieldChange('loc_title', e.target.value)}
                                />
                                <label className={labelClass}>Descripción</label>
                                <textarea 
                                    className={textareaClass}
                                    value={draftContent.loc_text || ''}
                                    onChange={(e) => handleFieldChange('loc_text', e.target.value)}
                                />
                            </div>
                        </div>
                        <div>
                            <span className="text-[10px] text-[#00d4ff] font-bold tracking-[0.2em] uppercase">Bloque 03: Seguridad</span>
                            <div className="mt-2 space-y-2">
                                <label className={labelClass}>Título</label>
                                <input 
                                    type="text"
                                    className={inputClass}
                                    value={draftContent.safe_title || ''}
                                    onChange={(e) => handleFieldChange('safe_title', e.target.value)}
                                />
                                <label className={labelClass}>Descripción</label>
                                <textarea 
                                    className={textareaClass}
                                    value={draftContent.safe_text || ''}
                                    onChange={(e) => handleFieldChange('safe_text', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'basecamp':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className={labelClass}>Categoría (Texto pequeño superior)</label>
                            <input 
                                type="text"
                                className={inputClass}
                                value={draftContent.badge || ''}
                                onChange={(e) => handleFieldChange('badge', e.target.value)}
                                placeholder="Nuestro Campamento Base"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Título Principal</label>
                            <input 
                                type="text"
                                className={inputClass}
                                value={draftContent.title || ''}
                                onChange={(e) => handleFieldChange('title', e.target.value)}
                                placeholder="Villa Cerro Castillo"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Descripción de la base</label>
                            <textarea 
                                className={textareaClass}
                                value={draftContent.text || ''}
                                onChange={(e) => handleFieldChange('text', e.target.value)}
                                placeholder="Ubicada a 95km de Coyhaique..."
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Texto del Botón (CTA)</label>
                            <input 
                                type="text"
                                className={inputClass}
                                value={draftContent.cta || ''}
                                onChange={(e) => handleFieldChange('cta', e.target.value)}
                                placeholder="Explorar Catálogo"
                            />
                        </div>
                    </div>
                );
            case 'instagram':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className={labelClass}>Categoría (Texto pequeño superior)</label>
                            <input 
                                type="text"
                                className={inputClass}
                                value={draftContent.badge || ''}
                                onChange={(e) => handleFieldChange('badge', e.target.value)}
                                placeholder="INSTAGRAM"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Título Principal</label>
                            <input 
                                type="text"
                                className={inputClass}
                                value={draftContent.title || ''}
                                onChange={(e) => handleFieldChange('title', e.target.value)}
                                placeholder="FEED DE INSTAGRAM"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Texto de Enlace (Llamado a la acción)</label>
                            <input 
                                type="text"
                                className={inputClass}
                                value={draftContent.cta || ''}
                                onChange={(e) => handleFieldChange('cta', e.target.value)}
                                placeholder="SÍGUENOS @FISURAEXPEDITIONS"
                            />
                        </div>
                    </div>
                );
            case 'footer':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className={labelClass}>Manifiesto de Marca (Texto bajo el logo de Fisura Expeditions)</label>
                            <textarea 
                                className={textareaClass}
                                rows={4}
                                value={draftContent.manifesto || ''}
                                onChange={(e) => handleFieldChange('manifesto', e.target.value)}
                                placeholder="Nuestro compromiso se centra en la montaña y la autenticidad..."
                            />
                            <p className="text-[10px] text-gray-500 mt-1">
                                Este es el texto que aparece en la columna izquierda del pie de página de todo el sitio.
                            </p>
                        </div>
                        <div>
                            <label className={labelClass}>Ubicación</label>
                            <input 
                                type="text"
                                className={inputClass}
                                value={draftContent.location || ''}
                                onChange={(e) => handleFieldChange('location', e.target.value)}
                                placeholder="Villa Cerro Castillo, Aysén, Chile"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Correo de Contacto</label>
                            <input 
                                type="email"
                                className={inputClass}
                                value={draftContent.email || ''}
                                onChange={(e) => handleFieldChange('email', e.target.value)}
                                placeholder="hello@fisuraexpeditions.com"
                            />
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    // Render Preview Frame
    const renderPreview = () => {
        const bgImg = draftBgImage || 'https://via.placeholder.com/1200x800?text=Sin+Imagen';

        if (activeTab === 'about') {
            return (
                <div className="w-full h-full bg-[#0a0a0a] text-white flex flex-col justify-start p-5 overflow-y-auto text-center space-y-4">
                    {/* Header */}
                    <div>
                        <span className="text-[7px] font-bold text-[#00d4ff] uppercase tracking-[0.2em] block mb-1">
                            {draftAbout.category || 'Nuestra Historia'}
                        </span>
                        <h2 className="text-xs font-title uppercase tracking-tighter text-white leading-tight">
                            {draftAbout.title || 'NACIMOS EN EL GRANITO DE AYSÉN'}
                        </h2>
                        <div className="w-8 h-0.5 bg-[#00d4ff]/60 mx-auto mt-2" />
                    </div>

                    {/* Content */}
                    <div className="text-left space-y-3 max-w-[90%] mx-auto">
                        <p className="text-[8px] font-light leading-relaxed text-gray-300 whitespace-pre-wrap">
                            {draftAbout.text || 'Nos especializamos en experiencias personalizadas...'}
                        </p>

                        {(draftPhilosophy.phil_title || draftPhilosophy.phil_text) && (
                            <div className="pt-2 border-t border-white/10 space-y-1">
                                <h3 className="text-[8.5px] font-title uppercase text-[#00d4ff] tracking-wider leading-tight">
                                    {draftPhilosophy.phil_title || 'Nuestra filosofía'}
                                </h3>
                                <p className="text-[7.5px] text-gray-400 font-light leading-relaxed">
                                    {draftPhilosophy.phil_text}
                                </p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/10">
                            <div>
                                <h4 className="text-[7px] font-bold text-[#00d4ff] uppercase tracking-wider mb-0.5">
                                    {draftPhilosophy.loc_title || 'Ubicación'}
                                </h4>
                                <p className="text-[6.5px] text-gray-400 font-light leading-relaxed">
                                    {draftPhilosophy.loc_text}
                                </p>
                            </div>
                            <div>
                                <h4 className="text-[7px] font-bold text-[#00d4ff] uppercase tracking-wider mb-0.5">
                                    {draftPhilosophy.safe_title || 'Seguridad'}
                                </h4>
                                <p className="text-[6.5px] text-gray-400 font-light leading-relaxed">
                                    {draftPhilosophy.safe_text}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Carousel Photos preview */}
                    <div className="pt-3 border-t border-white/10 space-y-1.5 text-center">
                        <span className="text-[6.5px] font-bold text-gray-500 uppercase tracking-wider block">Galería ({(draftAbout.gallery || []).length} fotos)</span>
                        <div className="flex gap-2 justify-center overflow-x-auto py-1">
                            {draftAbout.gallery && draftAbout.gallery.length > 0 ? (
                                draftAbout.gallery.map((url: string, i: number) => (
                                    <img key={i} src={url} className="w-12 h-8 object-cover rounded border border-white/10 shrink-0" alt="" />
                                ))
                            ) : (
                                <div className="text-[6px] text-gray-600 uppercase tracking-widest py-1">Sin fotos en la galería (Se usarán paisajes por defecto)</div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        switch (activeSectionId) {
            case 'hero':
                return (
                    <div className="relative w-full h-full bg-black overflow-hidden flex flex-col justify-between p-6">
                        {/* Background */}
                        <div className="absolute inset-0">
                            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/50 z-10" />
                            <img 
                                src={bgImg} 
                                className="absolute inset-0 w-full h-full object-cover object-center"
                                alt="Preview" 
                            />
                        </div>
                        {/* Logo header */}
                        <div className="relative z-20 flex justify-between items-center">
                            <span className="text-[10px] text-white/50 tracking-widest font-black uppercase">FISURA EXPEDITIONS</span>
                            <div className="flex gap-2">
                                <span className="w-4 h-0.5 bg-white"></span>
                                <span className="w-4 h-0.5 bg-white"></span>
                            </div>
                        </div>
                        {/* Hero content */}
                        <div className="relative z-20 mt-auto space-y-4 text-left">
                            <p className="text-[11px] font-light text-[#f2f2f2]/90 max-w-[80%] uppercase tracking-wider drop-shadow-md">
                                {draftContent.tagline || 'Escribe un tagline...'}
                            </p>
                            <div className="flex items-center space-x-3 text-[8px] font-bold uppercase tracking-[0.2em] text-white">
                                <span>{draftContent.cta || 'Botón'}</span>
                                <span className="w-10 h-px bg-white"></span>
                            </div>
                        </div>
                    </div>
                );
            case 'about':
                // Default Home page about preview (light bg)
                return (
                    <div className="w-full h-full bg-[#fcfcfc] text-[#1a1a1a] flex flex-col justify-center items-center p-8 overflow-y-auto">
                        <span className="text-[8px] font-bold uppercase tracking-[0.3em] text-gray-400 mb-2">
                            {draftContent.category || 'Categoría'}
                        </span>
                        <h2 className="text-sm font-title uppercase tracking-tighter text-black text-center mb-4 leading-snug">
                            {draftContent.title || 'Título de Presentación'}
                        </h2>
                        <div className="w-8 h-px bg-black/20 mb-4" />
                        <p className="text-[10px] font-light text-center leading-relaxed text-gray-600 font-serif italic max-w-[90%]">
                            "{draftContent.text || 'Descripción de sobre nosotros...'}"
                        </p>
                    </div>
                );
            case 'philosophy':
                // Default Home page philosophy preview (concept cards)
                return (
                    <div className="w-full h-full bg-[#0a0a0a] flex flex-col justify-start p-4 overflow-y-auto space-y-3 text-left">
                        <span className="text-[7px] font-bold uppercase tracking-[0.3em] text-gray-400 text-center block">Nuestros Conceptos (Home)</span>
                        {/* Concept 1 */}
                        <div className="relative h-[80px] w-full overflow-hidden rounded shadow-md flex items-center px-4 bg-gray-900">
                            <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent z-10" />
                            <img src="https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Cerro%20Colorado/cerro-colorado-mountain-panorama.jpg" className="absolute inset-0 w-full h-full object-cover brightness-50" />
                            <div className="relative z-20 text-left">
                                <span className="text-[#00d4ff] text-[10px] font-title block">01</span>
                                <h4 className="text-[9px] font-title text-white uppercase tracking-wider">{draftContent.phil_title || 'Nuestra filosofía'}</h4>
                                <p className="text-[7px] text-gray-300 max-w-[70%] truncate">{draftContent.phil_text}</p>
                            </div>
                        </div>
                        {/* Concept 2 */}
                        <div className="relative h-[80px] w-full overflow-hidden rounded shadow-md flex items-center px-4 bg-gray-900 justify-end text-right">
                            <div className="absolute inset-0 bg-gradient-to-l from-black/80 to-transparent z-10" />
                            <img src="https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Travesia%20Las%20Horquetas/travesia-las-horquetas-drone-aysen.jpg" className="absolute inset-0 w-full h-full object-cover brightness-50" />
                            <div className="relative z-20">
                                <span className="text-[#00d4ff] text-[10px] font-title block">02</span>
                                <h4 className="text-[9px] font-title text-white uppercase tracking-wider">{draftContent.loc_title || 'Ubicación Estratégica'}</h4>
                                <p className="text-[7px] text-gray-300 max-w-[70%] truncate ml-auto">{draftContent.loc_text}</p>
                            </div>
                        </div>
                        {/* Concept 3 */}
                        <div className="relative h-[80px] w-full overflow-hidden rounded shadow-md flex items-center px-4 bg-gray-900">
                            <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent z-10" />
                            <img 
                                src="https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Climbing%20Day/rock-climbing-walls-panorama.jpg" 
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = '/images/Climbing Day/rock-climbing-walls-panorama.jpg';
                                }}
                                className="absolute inset-0 w-full h-full object-cover brightness-50" 
                            />
                            <div className="relative z-20 text-left">
                                <span className="text-[#00d4ff] text-[10px] font-title block">03</span>
                                <h4 className="text-[9px] font-title text-white uppercase tracking-wider">{draftContent.safe_title || 'Seguridad Activa'}</h4>
                                <p className="text-[7px] text-gray-300 max-w-[70%] truncate">{draftContent.safe_text}</p>
                            </div>
                        </div>
                    </div>
                );
            case 'basecamp':
                return (
                    <div className="relative w-full h-full bg-black overflow-hidden flex items-center p-6 text-left">
                        <div className="absolute inset-0">
                            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent z-10" />
                            <img src={bgImg} className="absolute inset-0 w-full h-full object-cover" />
                        </div>
                        <div className="relative z-20 max-w-[80%] space-y-2">
                            <span className="text-[7px] font-bold uppercase tracking-[0.3em] text-[#00d4ff] block">{draftContent.badge || 'Categoría'}</span>
                            <h3 className="text-sm font-title text-white uppercase tracking-tighter leading-none">{draftContent.title || 'Base Camp'}</h3>
                            <p className="text-[8px] text-gray-300 font-light leading-relaxed">{draftContent.text}</p>
                            <div className="pt-2">
                                <span className="inline-block border border-white text-white text-[7px] tracking-widest uppercase px-3 py-1 font-title">{draftContent.cta || 'Botón'}</span>
                            </div>
                        </div>
                    </div>
                );
            case 'instagram':
                return (
                    <div className="w-full h-full bg-[#f9f9f9] text-[#1a1a1a] flex flex-col justify-between p-6 overflow-y-auto">
                        <div className="text-center space-y-1">
                            <span className="text-[7px] font-bold uppercase tracking-[0.3em] text-[#00d4ff] block">{draftContent.badge || 'INSTAGRAM'}</span>
                            <h3 className="text-xs font-title text-black uppercase tracking-tighter">{draftContent.title || 'Feed'}</h3>
                        </div>
                        {/* Mock Grid */}
                        <div className="grid grid-cols-3 gap-2 my-4">
                            <div className="aspect-square bg-gray-300 rounded-sm"></div>
                            <div className="aspect-square bg-gray-300 rounded-sm"></div>
                            <div className="aspect-square bg-gray-300 rounded-sm"></div>
                        </div>
                        <div className="text-center">
                            <span className="text-[7px] font-bold tracking-[0.2em] text-gray-600 border-b border-black/20 pb-0.5 uppercase">{draftContent.cta || 'Surgir'}</span>
                        </div>
                    </div>
                );
            case 'footer':
                return (
                    <div className="w-full h-full bg-[#050505] text-white flex flex-col justify-center p-6 overflow-y-auto text-left space-y-4 border-t border-rock">
                        <div className="space-y-1">
                            <h3 className="text-xs font-title uppercase tracking-tighter text-white">FISURA EXPEDITIONS</h3>
                            <div className="w-6 h-0.5 bg-[#00d4ff]/60" />
                        </div>
                        <p className="text-[9px] text-gray-300 font-light leading-relaxed uppercase tracking-[0.15em] border-l border-[#00d4ff]/40 pl-3">
                            {draftContent.manifesto || 'Nuestro compromiso se centra en la montaña y la autenticidad...'}
                        </p>
                        <div className="space-y-1 text-[8px] text-gray-400">
                            <div>📍 {draftContent.location || 'Villa Cerro Castillo, Aysén, Chile'}</div>
                            <div>✉️ {draftContent.email || 'hello@fisuraexpeditions.com'}</div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="h-[60vh] flex flex-col items-center justify-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-[#00d4ff]" />
                <span className="text-xs uppercase tracking-[0.2em] text-gray-500">Cargando secciones...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100/10 pb-6">
                <div>
                    <h1 className={`text-2xl font-title uppercase tracking-wider flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <Layout className="w-6 h-6 text-[#00d4ff]" />
                        Editor de Páginas Web
                    </h1>
                    <p className="text-xs text-gray-500 mt-1">
                        Modifica los contenidos, visibilidad e imágenes del sitio web en tiempo real.
                    </p>
                </div>
                
                {/* Save button */}
                <button 
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center justify-center gap-2 bg-[#00d4ff] hover:bg-[#00b0d6] text-black font-title uppercase tracking-widest text-xs px-6 py-3 rounded-[8px] transition-all font-bold shadow-lg shadow-[#00d4ff]/10 disabled:opacity-50"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Guardar Sección
                </button>
            </div>

            {/* Premium Segmented Tabs Selector */}
            <div className={`flex p-1 rounded-[10px] w-full sm:w-fit ${isDark ? 'bg-black/40 border border-rock' : 'bg-gray-100 border border-gray-200'}`}>
                <button
                    onClick={() => {
                        setActiveTab('home');
                        handleSelectSection('hero');
                    }}
                    className={`flex-1 sm:flex-initial text-center px-6 py-2.5 text-xs font-bold uppercase tracking-widest transition-all rounded-[8px] ${
                        activeTab === 'home'
                            ? 'bg-[#00d4ff] text-black shadow-md'
                            : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-black/5'
                    }`}
                >
                    Página Inicio (Home)
                </button>
                <button
                    onClick={() => {
                        setActiveTab('about');
                        const aboutSec = sections.find(s => s.id === 'about');
                        const philSec = sections.find(s => s.id === 'philosophy');
                        if (aboutSec) setDraftAbout(aboutSec.content_es || {});
                        if (philSec) setDraftPhilosophy(philSec.content_es || {});
                    }}
                    className={`flex-1 sm:flex-initial text-center px-6 py-2.5 text-xs font-bold uppercase tracking-widest transition-all rounded-[8px] ${
                        activeTab === 'about'
                            ? 'bg-[#00d4ff] text-black shadow-md'
                            : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-black/5'
                    }`}
                >
                    Página Nosotros (About)
                </button>
            </div>

            {/* Notification alert */}
            {notification && (
                <div className={`p-4 rounded-[8px] flex items-center gap-3 border animate-in fade-in slide-in-from-top duration-300 ${
                    notification.type === 'success' 
                    ? 'bg-green-500/10 border-green-500/30 text-green-500' 
                    : 'bg-red-500/10 border-red-500/30 text-red-500'
                }`}>
                    {notification.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    <span className="text-xs font-bold uppercase tracking-widest">{notification.message}</span>
                </div>
            )}

            {/* Main Split Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                {activeTab === 'home' ? (
                    <>
                        {/* 1. SECTIONS LIST SIDEBAR (3 cols) */}
                        <div className="lg:col-span-3 space-y-3">
                            <p className={labelClass}>Secciones de la Home</p>
                            <div className="flex flex-col gap-1">
                                {[
                                    { id: 'hero', label: '1. Hero Principal', desc: 'Imagen superior y llamada a la acción' },
                                    { id: 'philosophy', label: '2. Experiencia y Conceptos', desc: 'Filosofía, ubicación y seguridad' },
                                    { id: 'basecamp', label: '3. Campamento Base', desc: 'Sección parallax Villa Cerro Castillo' },
                                    { id: 'instagram', label: '4. Instagram Grid', desc: 'Llamada al feed oficial' },
                                    { id: 'footer', label: '5. Pie de Página (Footer)', desc: 'Manifiesto de marca, ubicación y correo' }
                                ].map((sec) => {
                                    const actualSec = sections.find(s => s.id === sec.id);
                                    const isSelected = sec.id === activeSectionId;
                                    const isVisible = actualSec ? actualSec.is_visible : true;
                                    
                                    return (
                                        <button
                                            key={sec.id}
                                            onClick={() => handleSelectSection(sec.id)}
                                            className={`flex items-start justify-between w-full text-left p-4 rounded-[8px] border transition-all duration-300 group ${
                                                isSelected 
                                                    ? 'bg-[#00d4ff]/10 border-[#00d4ff] text-[#00d4ff]' 
                                                    : isDark 
                                                        ? 'bg-[#1a1512] border-rock hover:bg-white/5 text-gray-300' 
                                                        : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm'
                                            }`}
                                        >
                                            <div className="space-y-1">
                                                <span className="text-xs font-bold uppercase tracking-wider block">{sec.label}</span>
                                                <span className="text-[9px] text-gray-500 block leading-tight">{sec.desc}</span>
                                            </div>
                                            <div className="flex items-center gap-2 pt-0.5">
                                                {isVisible ? (
                                                    <Eye className={`w-3.5 h-3.5 ${isSelected ? 'text-[#00d4ff]' : 'text-gray-400'}`} />
                                                ) : (
                                                    <EyeOff className="w-3.5 h-3.5 text-red-500/50" />
                                                )}
                                                <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-300 ${
                                                    isSelected ? 'translate-x-1 text-[#00d4ff]' : 'text-gray-400'
                                                }`} />
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 2. EDITOR FORM PANEL (5 cols) */}
                        <div className={`lg:col-span-5 border rounded-[12px] p-6 space-y-6 flex flex-col justify-between transition-colors duration-500 ${
                            isDark ? 'bg-[#1a1512] border-rock' : 'bg-white border-gray-200 shadow-xl'
                        }`}>
                            <div className="space-y-6">
                                {/* Section Header */}
                                <div className="flex items-center justify-between border-b border-gray-100/10 pb-4">
                                    <div>
                                        <span className="text-[9px] text-[#00d4ff] font-bold uppercase tracking-[0.3em]">Editar Contenido</span>
                                        <h3 className={`text-base font-title uppercase tracking-wide ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                            {activeSectionId.toUpperCase()}
                                        </h3>
                                    </div>
                                    
                                    {/* Visibility switch */}
                                    <button
                                        onClick={() => setDraftIsVisible(prev => !prev)}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-[6px] border text-[9px] font-bold uppercase tracking-widest transition-all ${
                                            draftIsVisible
                                                ? 'bg-green-500/10 border-green-500/30 text-green-500'
                                                : 'bg-red-500/10 border-red-500/30 text-red-500'
                                        }`}
                                    >
                                        {draftIsVisible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                                        {draftIsVisible ? 'Activa' : 'Oculta'}
                                    </button>
                                </div>

                                <div className="flex items-center gap-2 px-3 py-2 border rounded-[8px] bg-[#00d4ff]/5 border-[#00d4ff]/20 text-[#00d4ff] text-[10px] font-bold uppercase tracking-wider">
                                    <Globe className="w-3.5 h-3.5" />
                                    <span>Editando en Español (Se traducirá automáticamente al guardar)</span>
                                </div>

                                {renderFormFields()}

                                {['hero', 'basecamp'].includes(activeSectionId) && (
                                    <div className="space-y-3 pt-2 border-t border-gray-100/10">
                                        <label className={labelClass}>Imagen de Fondo</label>
                                        {draftBgImage && (
                                            <div className="relative aspect-video rounded-[8px] overflow-hidden border border-gray-100/10 group bg-black">
                                                <img src={draftBgImage} className="w-full h-full object-cover opacity-80" alt="Background" />
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <span className="text-[10px] text-white uppercase font-bold tracking-widest bg-black/60 px-3 py-1.5 rounded border border-white/20">Imagen Activa</span>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div className="flex gap-4 items-center">
                                            <label className={`flex-1 flex flex-col items-center justify-center border-2 border-dashed rounded-[8px] p-4 cursor-pointer hover:border-[#00d4ff]/50 transition-all ${
                                                isDark ? 'border-rock bg-black' : 'border-gray-300 bg-gray-50'
                                            }`}>
                                                {uploading ? (
                                                    <Loader2 className="w-5 h-5 animate-spin text-[#00d4ff]" />
                                                ) : (
                                                    <Upload className="w-5 h-5 text-gray-500" />
                                                )}
                                                <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mt-2">
                                                    {uploading ? 'Subiendo...' : 'Subir nueva imagen'}
                                                </span>
                                                <input 
                                                    type="file" 
                                                    accept="image/*" 
                                                    className="hidden" 
                                                    onChange={handleImageUpload}
                                                    disabled={uploading}
                                                />
                                            </label>
                                            
                                            {draftBgImage && (
                                                <button 
                                                    onClick={() => setDraftBgImage('')}
                                                    className="text-[9px] font-bold uppercase tracking-widest text-red-500/70 hover:text-red-500 border border-red-500/20 px-3 py-2 rounded hover:bg-red-500/5 transition-all"
                                                >
                                                    Quitar Foto
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="pt-6 border-t border-gray-100/10 flex justify-between items-center text-[8px] font-bold tracking-widest text-gray-500 uppercase mt-6">
                                <span>Estado: {activeSection ? 'Sincronizado' : 'Sin Guardar'}</span>
                                {activeSection?.updated_at && (
                                    <span>Modificado: {new Date(activeSection.updated_at).toLocaleDateString()}</span>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    // Nosotros Tab - Unified Editor Form Panel (occupies 8 columns!)
                    <div className={`lg:col-span-8 border rounded-[12px] p-6 space-y-6 flex flex-col justify-between transition-colors duration-500 ${
                        isDark ? 'bg-[#1a1512] border-rock' : 'bg-white border-gray-200 shadow-xl'
                    }`}>
                        <div className="space-y-6">
                            <div className="flex items-center justify-between border-b border-gray-100/10 pb-4">
                                <div>
                                    <span className="text-[9px] text-[#00d4ff] font-bold uppercase tracking-[0.3em]">Editar Página Completa</span>
                                    <h3 className={`text-base font-title uppercase tracking-wide ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        Nosotros (About)
                                    </h3>
                                </div>
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-[6px] border text-[9px] font-bold uppercase tracking-widest bg-green-500/10 border-green-500/30 text-green-500">
                                    <Globe className="w-3.5 h-3.5" />
                                    <span>Pestaña Activa</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 px-3 py-2 border rounded-[8px] bg-[#00d4ff]/5 border-[#00d4ff]/20 text-[#00d4ff] text-[10px] font-bold uppercase tracking-wider">
                                <Globe className="w-3.5 h-3.5" />
                                <span>Editando en Español (Se traducirá automáticamente al guardar)</span>
                            </div>

                            <div className="space-y-8">
                                {/* Bloque 1: Historia (Sobre Nosotros) */}
                                <div className="space-y-4 border-b border-gray-100/10 pb-6">
                                    <span className="text-[10px] text-[#00d4ff] font-bold tracking-[0.2em] uppercase block">1. Historia (Sobre Nosotros)</span>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelClass}>Categoría (Texto superior)</label>
                                            <input 
                                                type="text"
                                                className={inputClass}
                                                value={draftAbout.category || ''}
                                                onChange={(e) => handleAboutFieldChange('category', e.target.value)}
                                                placeholder="Nuestra Historia"
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Título Destacado</label>
                                            <input 
                                                type="text"
                                                className={inputClass}
                                                value={draftAbout.title || ''}
                                                onChange={(e) => handleAboutFieldChange('title', e.target.value)}
                                                placeholder="NACIMOS EN EL GRANITO DE AYSÉN"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className={labelClass}>Descripción</label>
                                        <textarea 
                                            className={`${textareaClass} min-h-[120px]`}
                                            value={draftAbout.text || ''}
                                            onChange={(e) => handleAboutFieldChange('text', e.target.value)}
                                            placeholder="Detalla la historia..."
                                        />
                                    </div>
                                </div>

                                {/* Bloque 2: Filosofía, Ubicación y Seguridad */}
                                <div className="space-y-6">
                                    <span className="text-[10px] text-[#00d4ff] font-bold tracking-[0.2em] uppercase block">2. Filosofía y Ecosistema</span>
                                    <div className="space-y-4">
                                        <div>
                                            <label className={labelClass}>Título Filosofía</label>
                                            <input 
                                                type="text"
                                                className={inputClass}
                                                value={draftPhilosophy.phil_title || ''}
                                                onChange={(e) => handlePhilosophyFieldChange('phil_title', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Descripción Filosofía</label>
                                            <textarea 
                                                className={`${textareaClass} min-h-[70px]`}
                                                value={draftPhilosophy.phil_text || ''}
                                                onChange={(e) => handlePhilosophyFieldChange('phil_text', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Ubicación */}
                                        <div className={`p-4 border rounded-[8px] space-y-3 bg-black/20 border-rock`}>
                                            <span className="text-[9px] text-[#00d4ff] font-bold tracking-wider uppercase block">A. Ubicación</span>
                                            <div>
                                                <label className={labelClass}>Título Ubicación</label>
                                                <input 
                                                    type="text"
                                                    className={inputClass}
                                                    value={draftPhilosophy.loc_title || ''}
                                                    onChange={(e) => handlePhilosophyFieldChange('loc_title', e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Descripción Ubicación</label>
                                                <textarea 
                                                    className={`${textareaClass} min-h-[100px]`}
                                                    value={draftPhilosophy.loc_text || ''}
                                                    onChange={(e) => handlePhilosophyFieldChange('loc_text', e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        {/* Seguridad */}
                                        <div className={`p-4 border rounded-[8px] space-y-3 bg-black/20 border-rock`}>
                                            <span className="text-[9px] text-[#00d4ff] font-bold tracking-wider uppercase block">B. Seguridad</span>
                                            <div>
                                                <label className={labelClass}>Título Seguridad</label>
                                                <input 
                                                    type="text"
                                                    className={inputClass}
                                                    value={draftPhilosophy.safe_title || ''}
                                                    onChange={(e) => handlePhilosophyFieldChange('safe_title', e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Descripción Seguridad</label>
                                                <textarea 
                                                    className={`${textareaClass} min-h-[100px]`}
                                                    value={draftPhilosophy.safe_text || ''}
                                                    onChange={(e) => handlePhilosophyFieldChange('safe_text', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bloque 3: Galería del Carrusel */}
                            <div className="border-t pt-6 mt-6 border-gray-100/10 space-y-3">
                                <span className="text-[10px] text-[#00d4ff] font-bold tracking-[0.2em] uppercase block">3. Fotos del Carrusel</span>
                                <p className="text-[8px] text-gray-500 uppercase tracking-wider mb-2">Sube las fotos que aparecerán en el carrusel de imágenes al final de la página Nosotros.</p>
                                
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                    <label className="aspect-video border-2 border-dashed border-gray-100/20 hover:border-[#00d4ff] flex flex-col items-center justify-center cursor-pointer rounded-[8px] hover:bg-[#00d4ff]/5 transition-all text-gray-500 hover:text-[#00d4ff] bg-black/10">
                                        {uploadingAboutImages ? <Loader2 className="w-5 h-5 animate-spin text-[#00d4ff]" /> : <Upload className="w-5 h-5" />}
                                        <span className="text-[8px] font-bold uppercase tracking-widest mt-1.5">{uploadingAboutImages ? 'Subiendo...' : 'Subir Fotos'}</span>
                                        <input type="file" multiple accept="image/jpeg,image/png,image/webp,image/jpg" className="hidden" onChange={handleAboutImageUpload} disabled={uploadingAboutImages} />
                                    </label>
                                    
                                    {(draftAbout.gallery || []).map((url: string, index: number) => (
                                        <div key={url} className="aspect-video relative group rounded-[8px] overflow-hidden border border-gray-100/10 bg-black/20">
                                            <img src={url} className="w-full h-full object-cover" alt="" />
                                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                <button 
                                                    type="button"
                                                    onClick={() => removeAboutImage(url)} 
                                                    className="p-1.5 bg-red-500/80 hover:bg-red-600 rounded-full text-white transition-all text-xs"
                                                    title="Eliminar foto"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="pt-6 border-t border-gray-100/10 flex justify-between items-center text-[8px] font-bold tracking-widest text-gray-500 uppercase mt-4">
                            <span>Estado: Sincronizado</span>
                            <span>Tipo de plantilla: Nosotros (About)</span>
                        </div>
                    </div>
                )}

                {/* 3. SIMULATOR PREVIEW PANEL (4 cols) */}
                <div className="lg:col-span-4 space-y-3">
                    <div className="flex flex-col gap-1.5">
                        <div className="flex items-center justify-between">
                            <p className={labelClass}>Previsualización en Vivo</p>
                            
                            {/* Device selector tabs */}
                            <div className="flex border rounded-[8px] overflow-hidden p-0.5 border-gray-100/10 bg-black/10">
                                <button
                                    onClick={() => setPreviewMode('mobile')}
                                    className={`p-1.5 rounded-[6px] transition-all ${
                                        previewMode === 'mobile'
                                            ? 'bg-[#00d4ff] text-black'
                                            : 'text-gray-500 hover:text-gray-300'
                                    }`}
                                    title="Vista Móvil"
                                >
                                    <Smartphone className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setPreviewMode('desktop')}
                                    className={`p-1.5 rounded-[6px] transition-all ${
                                        previewMode === 'desktop'
                                            ? 'bg-[#00d4ff] text-black'
                                            : 'text-gray-500 hover:text-gray-300'
                                    }`}
                                    title="Vista Escritorio"
                                >
                                    <Monitor className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-[8px] text-[12px] leading-relaxed flex items-start gap-2">
                            <span className="shrink-0 text-red-500 font-bold mt-0.5">⚠️</span>
                            <span>
                                <strong>Borrador de diseño</strong>: Esto es solo una previsualización rápida. Para ver la versión final interactiva, debes guardar los cambios y revisar la página web.
                            </span>
                        </div>
                    </div>

                    {/* Simulated Screen Body */}
                    <div className={`relative border rounded-[20px] overflow-hidden flex flex-col justify-center items-center bg-black/40 shadow-2xl transition-all duration-500 border-gray-100/10 ${
                        previewMode === 'mobile' 
                            ? 'w-[270px] mx-auto aspect-[9/16] h-[480px]' 
                            : 'w-full aspect-[4/3] h-[360px] lg:h-[400px]'
                    }`}>
                        {/* Device Frame Details for mobile */}
                        {previewMode === 'mobile' && (
                            <div className="absolute top-2 w-24 h-4 rounded-full bg-black/80 z-30 flex items-center justify-center">
                                <div className="w-1.5 h-1.5 rounded-full bg-gray-800 mr-2"></div>
                                <div className="w-8 h-1 rounded-full bg-gray-800"></div>
                            </div>
                        )}

                        {/* Preview Screen Content Container */}
                        <div className={`w-full h-full relative z-10 transition-opacity duration-300 ${
                            draftIsVisible ? 'opacity-100' : 'opacity-30'
                        }`}>
                            {renderPreview()}

                            {/* Oculto Overlay */}
                            {!draftIsVisible && (
                                <div className="absolute inset-0 bg-black/60 z-30 flex flex-col justify-center items-center gap-2 select-none pointer-events-none">
                                    <EyeOff className="w-8 h-8 text-red-500" />
                                    <span className="text-[10px] font-title uppercase tracking-widest text-red-500 bg-black/80 px-3 py-1 rounded border border-red-500/20">Sección Desactivada</span>
                                    <span className="text-[8px] font-bold uppercase tracking-widest text-gray-400">No se mostrará en la web pública</span>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-1.5 justify-center text-[9px] text-gray-500 font-bold uppercase tracking-wider select-none">
                        <Sparkles className="w-3 h-3 text-[#00d4ff] animate-pulse" />
                        <span>Previsualización del diseño (Edición en Español)</span>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default HomeEditor;
